import { config } from '@/lib/config';
import type { ApiErrorBody, AuthTokens, ErrorCode, Ok } from './types';
import { clearSession, getRefreshToken, setTokens } from '@/lib/auth/session';
import { AppError } from './errors';

/**
 * Single-flight refresh (plan §4.2).
 *
 * Concurrent 401s share ONE in-flight refresh via a memoized promise; never two
 * concurrent refreshes. On success the new token pair is stored and the promise resolves.
 *
 * On failure the question that matters is *which* failure. Only a refusal that says the
 * credential itself is no good — 401, or 403 from reuse-detection family invalidation (see
 * refresh.md) — clears the session and fires the hard-logout handler.
 *
 * Everything else (429, 5xx, a dropped connection) is the server being temporarily unable
 * to answer, and logging an editor out for it is both wrong and expensive: the access token
 * lives in memory only, so a hard-logout throws away the tab and any unsaved editor state.
 * The auth endpoints are rate-limited per IP, so a few tabs or a handful of reloads in a
 * minute used to return a signed-in editor to the sign-in screen. So a transient failure
 * gets ONE short retry and is then reported as an error the screen can show — with the
 * session left intact. Never a loop.
 */
let inFlight: Promise<void> | null = null;
let onFailure: (() => void) | null = null;

export function setRefreshFailureHandler(fn: () => void): void {
  onFailure = fn;
}

/** Statuses that mean "the credential is dead", as opposed to "not right now". */
function isCredentialFailure(status: number): boolean {
  return status === 401 || status === 403;
}

/**
 * How long to wait before the single retry. Honours `Retry-After` (seconds, or an HTTP
 * date) but caps it: a 60-second window is longer than anyone will hold still for, and a
 * retry that far out is better spent as a visible error the user can act on.
 */
export const MAX_RETRY_DELAY_MS = 2_000;
const DEFAULT_RETRY_DELAY_MS = 500;

function retryDelay(res: Response | null): number {
  const header = res?.headers.get('Retry-After');
  if (!header) return DEFAULT_RETRY_DELAY_MS;
  const seconds = Number(header);
  const ms = Number.isFinite(seconds) ? seconds * 1_000 : Math.max(0, Date.parse(header) - Date.now());
  return Number.isFinite(ms) ? Math.min(ms, MAX_RETRY_DELAY_MS) : DEFAULT_RETRY_DELAY_MS;
}

/** One refresh attempt; `null` when the request never reached a response (offline, DNS). */
async function post(): Promise<Response | null> {
  // Low-level fetch (NOT the intercepted client) to avoid recursion. credentials:'include'
  // sends the httpOnly refresh cookie; body carries the token if the backend uses that mode.
  const refreshToken = getRefreshToken();
  try {
    return await fetch(`${config.apiBaseUrl}/admin/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(refreshToken ? { refresh_token: refreshToken } : {}),
    });
  } catch {
    return null;
  }
}

async function errorFor(res: Response, fallbackCode: ErrorCode, fallbackMessage: string) {
  let body: ApiErrorBody | null = null;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    /* an empty or non-JSON body is normal for a 502 from a proxy */
  }
  return body?.error
    ? AppError.fromEnvelope(body, res.status)
    : new AppError({ code: fallbackCode, message: fallbackMessage, status: res.status });
}

async function doRefresh(): Promise<void> {
  let res = await post();

  if (!res || (!res.ok && !isCredentialFailure(res.status))) {
    await new Promise((resolve) => setTimeout(resolve, retryDelay(res)));
    res = await post();
  }

  if (!res) {
    // Session deliberately left intact — see the module comment.
    throw AppError.network('Could not reach the server. Your session is still open — try again.');
  }

  if (!res.ok) {
    if (isCredentialFailure(res.status)) {
      clearSession();
      onFailure?.();
      throw await errorFor(res, 'unauthorized', 'Session expired.');
    }
    throw await errorFor(
      res,
      res.status === 429 ? 'rate_limited' : 'internal_error',
      res.status === 429
        ? 'Too many requests. Wait a moment and try again.'
        : 'Could not reach the server. Your session is still open — try again.',
    );
  }

  const json = (await res.json()) as Ok<AuthTokens>;
  setTokens(json.data);
}

/** Trigger (or join) the single in-flight refresh. */
export function refreshSession(): Promise<void> {
  if (!inFlight) {
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** True when a refresh failure means the credential is dead (and the session was cleared). */
export function isCredentialRefusal(err: unknown): boolean {
  return err instanceof AppError && (err.is('unauthorized') || err.is('forbidden'));
}
